import React from 'react';
import { X, Shield, Lock, Users, FileText } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface TermsOfServiceProps {
  onClose?: () => void;
}

export const TermsOfService: React.FC<TermsOfServiceProps> = ({ onClose }) => {
  const navigate = useNavigate();

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      // If no onClose prop, navigate back or to auth page
      if (window.history.length > 1) {
        navigate(-1);
      } else {
        navigate('/auth');
      }
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
              <Shield className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Terms of Service</h2>
              <p className="text-sm text-gray-500">Grit, Growth & Gratitude Platform</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6 space-y-6">
          {/* Last Updated */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Last Updated:</strong> {new Date().toLocaleDateString()}
            </p>
          </div>

          {/* Introduction */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <FileText className="w-5 h-5 mr-2 text-red-600" />
              1. Introduction
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                Welcome to Grit, Growth & Gratitude LLC ("GGG"), a wellness and resilience-training platform developed for first responders 
                and related professionals. These Terms of Service ("Terms") govern your access to our website, applications, and all associated 
                services ("Services").
              </p>
              <p>
                By using our Services, you acknowledge that you have read, understood, and agree to be bound by these Terms. If you do not agree, 
                discontinue use immediately.
              </p>
            </div>
          </section>

          {/* Acceptance of Terms */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Acceptance of Terms</h3>
            <div className="text-gray-700 space-y-3">
              <p>By creating an account or accessing the Services, you affirm that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You are at least 18 years of age, or are using the platform with parental or guardian consent.</li>
                <li>You have read and agree to these Terms and our Privacy Policy.</li>
                <li>You have the legal authority to enter into this agreement.</li>
              </ul>
            </div>
          </section>

          {/* User Accounts */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Users className="w-5 h-5 mr-2 text-red-600" />
              3. User Accounts
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                <strong>3.1 Account Creation</strong><br />
                You must provide accurate and complete information when creating your account.
              </p>
              <p>
                <strong>3.2 Security</strong><br />
                You are responsible for maintaining the confidentiality of your credentials and for all activity under your account. 
                Notify us immediately of unauthorized access.
              </p>
              <p>
                <strong>3.3 Department Access Codes</strong><br />
                Some features require valid department or organization access codes. You are responsible for ensuring you have legitimate 
                authorization to use these features.
              </p>
            </div>
          </section>

          {/* Acceptable Use */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Acceptable Use</h3>
            <div className="text-gray-700 space-y-3">
              <p><strong>You agree not to:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Upload, share, or store harmful, abusive, or inappropriate content</li>
                <li>Attempt to gain unauthorized access to any system or data</li>
                <li>Interfere with the platform's operation or security</li>
                <li>Share your login credentials</li>
                <li>Use the Services for commercial, competitive, or unauthorized purposes</li>
                <li>Reverse-engineer, copy, or redistribute any part of the platform</li>
              </ul>
            </div>
          </section>

          {/* Privacy & Data Protection */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center">
              <Lock className="w-5 h-5 mr-2 text-red-600" />
              5. Privacy & Data Protection
            </h3>
            <div className="text-gray-700 space-y-3">
              <p>
                Your privacy matters to us. Our data collection and usage practices are described in our Privacy Policy, which is incorporated 
                herein by reference.
              </p>
              <p>
                <strong>5.1 Sensitive Data</strong><br />
                GGG may process information related to mood, stress, wellness, or personal journaling. All sensitive data is encrypted and 
                handled according to applicable privacy laws.
              </p>
              <p>
                <strong>5.2 Journal Content</strong><br />
                Your private journal entries are not monitored or accessed by GGG, unless required:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>To comply with legal obligations</li>
                <li>To prevent or respond to imminent safety threats</li>
                <li>Pursuant to valid law enforcement or court orders</li>
              </ul>
            </div>
          </section>

          {/* Intellectual Property */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Intellectual Property</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                <strong>6.1 Ownership</strong><br />
                All platform content—including text, graphics, training materials, software, AI outputs, prompts, branding, logos, and 
                interactive features—is owned by GGG and protected by intellectual property laws.
              </p>
              <p>
                <strong>6.2 User Content</strong><br />
                You retain ownership of content you create within the Services. You grant GGG a limited license to store and process this 
                content strictly for providing and improving the Services.
              </p>
            </div>
          </section>

          {/* Disclaimers */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Disclaimers</h3>
            <div className="text-gray-700 space-y-4">
              <div>
                <p className="font-semibold mb-2">7.1 Non-Clinical Wellness Platform</p>
                <p className="mb-2">
                  GGG is a non-clinical, non-medical, non-therapeutic platform. It does not provide:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Medical care</li>
                  <li>Mental health treatment</li>
                  <li>Psychotherapy or counseling</li>
                  <li>Diagnosis or clinical assessment</li>
                  <li>Legal, operational, or emergency guidance</li>
                </ul>
                <p className="mt-2">GGG is for general educational and peer-support purposes only.</p>
              </div>

              <div>
                <p className="font-semibold mb-2">7.2 Not a Crisis Service</p>
                <p className="mb-2">GGG cannot and does not respond to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Suicidal thoughts</li>
                  <li>Intent to self-harm or harm others</li>
                  <li>Psychiatric emergencies</li>
                  <li>Acute mental health deterioration</li>
                </ul>
                <p className="mt-2 font-semibold">
                  If you are experiencing a crisis, call 911, your local emergency number, or the US 988 Suicide & Crisis Lifeline. 
                  Contact your department's peer-support or mental health professionals immediately.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">7.3 AI Limitations</p>
                <p className="mb-2">
                  GGG uses artificial intelligence to support journaling, reflections, prompts, and non-clinical peer-support content.
                </p>
                <p className="mb-2">You acknowledge and agree that:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>AI may produce inaccurate, outdated, incomplete, or misleading information</li>
                  <li>AI is not a licensed professional</li>
                  <li>AI content is not medical, therapeutic, legal, or operational advice</li>
                  <li>You should not rely on AI content to make decisions related to health, safety, or employment</li>
                  <li>You assume all responsibility for how you interpret or use AI-generated content</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-2">7.4 No Professional Relationship Created</p>
                <p>
                  Use of the Services does not create a therapist–patient relationship, physician–patient relationship, clinical duty, 
                  or fiduciary obligation.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">7.5 No Warranties</p>
                <p>
                  The Services are provided "as is" and "as available", without warranties of any kind—express or implied—including accuracy, 
                  reliability, fitness for a particular purpose, or uninterrupted availability.
                </p>
              </div>
            </div>
          </section>

          {/* Limitation of Liability */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Limitation of Liability</h3>
            <div className="text-gray-700 space-y-3">
              <p>To the fullest extent permitted by law, GGG and its owners, partners, employees, advisors, and affiliates are not liable for:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Emotional distress, psychological outcomes, or personal decisions</li>
                <li>Damages arising from reliance on AI-generated or platform-generated content</li>
                <li>Loss of data, business, revenue, or opportunity</li>
                <li>Any indirect, incidental, special, punitive, or consequential damages</li>
              </ul>
              <p className="mt-3">
                If liability cannot be fully disclaimed, the maximum liability of GGG shall not exceed the amount paid by you to use the 
                Services, if any.
              </p>
            </div>
          </section>

          {/* Indemnification */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Indemnification</h3>
            <div className="text-gray-700 space-y-3">
              <p>You agree to indemnify and hold harmless GGG and its affiliates from any claim, loss, liability, or expense arising from:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Your use or misuse of the Services</li>
                <li>Your violation of these Terms</li>
                <li>Your reliance on AI or other platform content</li>
              </ul>
            </div>
          </section>

          {/* Termination */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Termination</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                GGG may suspend or terminate your account at its discretion for violations of these Terms, threats to user safety, or misuse 
                of the platform.
              </p>
              <p>
                You may stop using the Services at any time. Sections related to disclaimers, liability, arbitration, and indemnification 
                survive termination.
              </p>
            </div>
          </section>

          {/* Changes to Terms */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">11. Changes to Terms</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                We may revise these Terms from time to time. Material changes will be communicated via email or in-app notification. 
                Continued use of the Services constitutes acceptance of updated Terms.
              </p>
            </div>
          </section>

          {/* Governing Law */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">12. Governing Law</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                These Terms are governed by the laws of the State of Arizona, without regard to conflict-of-law principles, and by applicable 
                federal law.
              </p>
            </div>
          </section>

          {/* Binding Arbitration Agreement */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">13. Binding Arbitration Agreement</h3>
            <div className="text-gray-700 space-y-4">
              <div>
                <p className="font-semibold mb-2">13.1 Agreement to Arbitrate</p>
                <p>
                  You and GGG agree that any dispute, claim, or controversy arising out of or relating to these Terms or your use of the 
                  Services ("Claims") will be resolved exclusively through binding individual arbitration, not in court.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">13.2 Exceptions</p>
                <p>This does not apply to:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Claims eligible for small claims court</li>
                  <li>Requests for temporary or emergency injunctive relief</li>
                  <li>Situations where arbitration agreements are prohibited by law</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold mb-2">13.3 Rules & Procedures</p>
                <p>
                  Arbitration will be administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules, 
                  conducted by a single arbitrator, and held virtually unless both parties agree otherwise.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">13.4 Governing Law</p>
                <p>This arbitration agreement is governed by the Federal Arbitration Act (FAA).</p>
              </div>

              <div>
                <p className="font-semibold mb-2">13.5 Fees</p>
                <p>
                  GGG will pay arbitration fees beyond the first $200 unless the arbitrator determines the claim is frivolous.
                </p>
              </div>

              <div>
                <p className="font-semibold mb-2">13.6 Arbitrator Authority</p>
                <p>The arbitrator may award individual relief only. No group, representative, or class relief is permitted.</p>
              </div>

              <div>
                <p className="font-semibold mb-2">13.7 Opt-Out</p>
                <p>You may opt out of arbitration within 30 days of creating your account by emailing:</p>
                <p className="font-semibold mt-1">legal@ggg.training</p>
              </div>
            </div>
          </section>

          {/* Class Action Waiver */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">14. Class Action Waiver</h3>
            <div className="text-gray-700 space-y-3">
              <p>To the fullest extent permitted by law, you agree that:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Claims must be brought individually</li>
                <li>No class actions</li>
                <li>No class arbitrations</li>
                <li>No representative actions</li>
                <li>No mass arbitration filings</li>
              </ul>
              <p className="mt-3">
                If this waiver is found unenforceable for a specific Claim, that Claim must be brought in court, not arbitration.
              </p>
            </div>
          </section>

          {/* Severability */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">15. Severability</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                If any part of these Terms is found invalid or unenforceable, the remainder will remain in effect. Unenforceable provisions 
                will be replaced with enforceable ones that best reflect the original intent.
              </p>
            </div>
          </section>

          {/* Entire Agreement */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">16. Entire Agreement</h3>
            <div className="text-gray-700 space-y-3">
              <p>
                These Terms constitute the entire agreement between you and GGG regarding the Services and supersede all prior understandings 
                or agreements.
              </p>
            </div>
          </section>

          {/* Contact Information */}
          <section>
            <h3 className="text-lg font-semibold text-gray-900 mb-3">17. Contact Information</h3>
            <div className="text-gray-700 space-y-3">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p><strong>Support:</strong> topher@echohealth.app</p>
                <p><strong>Training & Department Coordination:</strong> joe@ggg.training</p>
                <p className="mt-2">
                  <strong>Address:</strong><br />
                  PO BOX 7301<br />
                  Chandler, AZ 85246
                </p>
              </div>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              By using our platform, you agree to these Terms of Service.
            </p>
            <button
              onClick={handleClose}
              className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};














